'use client';
import DesktopNavigation from '@/features/navigation/components/desktop-navigation/desktop-navigation';
import InformationSection from '@/features/navigation/components/information-section/information-section';
import MainSection from '@/features/navigation/components/main-section/main-section';
import MobileMenu from '@/features/navigation/components/mobile-menu/mobile-menu';
import { Backdrop } from '@/features/navigation/components/navigation-extended/backdrop';
import {
  type NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import { type Breakpoint, useBreakpoint } from '@/hooks/use-theme';
import { StickyNavigationSpacer } from '@/navigation/components/navigation-extended/sticky-navigation-spacer';
import type { Navigation } from '@/navigation/types/navigation';
import { useEventListener, useThrottledCallback } from '@react-hookz/web';
import { clsx } from 'clsx';
import { useEffect } from 'react';
import FocusLock from 'react-focus-lock';

const selector = ({
  isMobileMenuOpen,
  navigationPath,
  categoryId,
}: NavigationStore) => ({
  isMobileMenuOpen,
  navigationPath,
  categoryId,
});

const stickyTopOffsets: Record<Breakpoint, number> = {
  xxs: 6,
  xs: 6,
  s: 6,
  m: 41,
  l: 41,
  xl: 41,
  xxl: 41,
};

interface Props {
  menu: Navigation[];
}

export const ClientNavigation = ({ menu }: Props) => {
  const { navigationPath, isMobileMenuOpen, categoryId } =
    useNavigationStore(selector);
  const activeBreakpoint = useBreakpoint();

  const toggleSticky = () => {
    if (navigationPath.length > 0 || categoryId) return;

    const offset = stickyTopOffsets[activeBreakpoint] || 0;
    if (window.scrollY > offset) {
      document.body.classList.add('sticky');
    } else {
      document.body.classList.remove('sticky');
    }
  };

  useEventListener(
    typeof document != 'undefined' ? document : null,
    'scroll',
    useThrottledCallback(toggleSticky, [], 33),
  );

  useEffect(() => {
    toggleSticky();
  }, []);

  return (
    <>
      <div className="h-xxs max-h-xxs min-h-xxs w-full bg-brand" />
      <>
        {/*!StickyNavigationSpacer mask is needed to prevent flickering when navigation becomes sticky/fixed and reduces total page height*/}
        <StickyNavigationSpacer />
        <nav
          //INFO: I had to change sticky to fixed, because when inspector was open, the sticky class was broken
          className="top-0 z-top m-0 h-fit w-full border-decorative border-b bg-main p-0 transition-all group-[.sticky]:fixed group-[.sticky]:top-0 group-[.sticky]:bg-main-transparent"
        >
          <InformationSection className="m:block hidden group-[.sticky]:hidden" />
          <MainSection />
          <DesktopNavigation menu={menu} />
        </nav>
        <Backdrop />
        <FocusLock
          autoFocus={false}
          group="mobile-menu"
          disabled={!isMobileMenuOpen}
          className={clsx('group-[.sticky]:!block block l:hidden')}
        >
          <MobileMenu menu={menu} />
        </FocusLock>
      </>
    </>
  );
};
